import React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@chapche/ui';
import { stepHasErrors } from '../../services/cookingStepConditionUtils';
import { CookingStep } from '../../services/recipeTypes';
import { Locale } from '../../services/commonTypes';
import { SortableDragHandleProps } from '../../shared/dnd/SortableList';

type CookingStepListItemProps = {
    step: CookingStep;
    index: number;
    selected: boolean;
    onSelect: (stepId: string) => void;
    dragHandle: SortableDragHandleProps;
};

const previewRu = (step: CookingStep): string => {
    const ru = (step.texts || []).find((item) => item.locale === Locale.LOCALE_RU);
    const text = (ru?.text || '').trim();
    if (!text) {
        return '—';
    }
    return text.length > 72 ? `${text.slice(0, 72)}…` : text;
};

const CookingStepListItem: React.FC<CookingStepListItemProps> = ({
    step,
    index,
    selected,
    onSelect,
    dragHandle,
}) => {
    const { i18n } = useTranslation();
    const conditionsCount = (step.conditions || []).length;
    const hasErrors = stepHasErrors(step);

    return (
        <div
            className={cn(
                'cooking-steps__list-item flex items-stretch',
                selected && 'cooking-steps__list-item_selected',
                hasErrors && 'cooking-steps__list-item_error',
            )}
            role="listitem"
        >
            <button
                type="button"
                className="cooking-steps__drag-handle flex w-28 shrink-0 cursor-grab items-center justify-center border-0 bg-transparent p-0 text-ink-muted touch-none active:cursor-grabbing"
                aria-label={i18n.t('app.page.cookingStep.dragHandle')}
                ref={dragHandle.setActivatorNodeRef}
                {...dragHandle.attributes}
                {...dragHandle.listeners}
            >
                <span aria-hidden="true">⋮⋮</span>
            </button>
            <button
                type="button"
                className="cooking-steps__list-select flex min-w-0 flex-1 cursor-pointer items-start gap-12 rounded-sm border-0 bg-transparent py-12 pr-12 pl-4 text-left text-ink"
                onClick={() => step.id && onSelect(step.id)}
            >
                <span className="cooking-steps__list-index w-24 shrink-0 pt-2 text-[13px] text-ink-muted">{index + 1}</span>
                <span className="cooking-steps__list-body flex min-w-0 flex-1 flex-col gap-4 overflow-hidden">
                    <span className="cooking-steps__list-code-row flex min-w-0 items-center gap-8">
                        <span className="cooking-steps__list-code min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-[14px] font-semibold text-dark-green">{step.stepCode || '—'}</span>
                        {hasErrors ? (
                            <span
                                className="cooking-steps__error-dot h-8 w-8 shrink-0 rounded-full bg-error"
                                title={i18n.t('app.page.cookingStep.validation.stepHasErrors')}
                                aria-label={i18n.t('app.page.cookingStep.validation.stepHasErrors')}
                            />
                        ) : null}
                    </span>
                    <span className="cooking-steps__list-preview overflow-hidden text-ellipsis whitespace-nowrap text-[13px] text-ink-muted">{previewRu(step)}</span>
                    <span className="cooking-steps__list-badges mt-2 flex flex-wrap gap-[6px] overflow-hidden">
                        {step.isReminder ? (
                            <span className="cooking-steps__badge cooking-steps__badge_reminder">
                                {i18n.t('app.page.cookingStep.badge.reminder')}
                            </span>
                        ) : null}
                        {conditionsCount > 0 ? (
                            <span className="cooking-steps__badge cooking-steps__badge_conditions">
                                {i18n.t('app.page.cookingStep.badge.conditions', { count: conditionsCount })}
                            </span>
                        ) : (
                            <span className="cooking-steps__badge cooking-steps__badge_always">
                                {i18n.t('app.page.cookingStep.badge.always')}
                            </span>
                        )}
                    </span>
                </span>
            </button>
        </div>
    );
};

export default CookingStepListItem;
